import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, FormGroup, Label, Input, Button } from 'reactstrap';
import CustomModal from '../../../../CustomModal';
import socketIOClient from 'socket.io-client';
import { ReactComponent as LoadingIcon } from '../../../../../Res/loadingSpinner.svg';
import { ReactComponent as CrossIcon } from '../../../../../Res/cross.svg';
import { toast } from 'react-toastify';
import { dataCenterShellUrls } from '../../../BoxOverview';
import { stopPcap, startPcap, downloadPcap } from '../../../../../../src/actions/EpikBoxes';
import { useDispatch } from 'react-redux';
import { useUnifiedFetch } from '../../../../../hooks/useUnifiedFetch';

const EpiRecModal = ({ isOpen, onCancel, obis, boxDetails, user }) => {
  const [selectedEPIs, setSelectedEPIs] = useState([]);
  const [remoteCommands, setRemoteCommands] = useState([]);
  const [commandsData, setCommandsData] = useState([]);
  const [selectedCommand, setSelectedCommand] = useState('');
  const [stateSocket, setStateSocket] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const token = localStorage.getItem('jwtToken') || null;
  const serialNumberDash = `${boxDetails.serialNumber}-${user._id}`;
  const dispatch = useDispatch();
  const epiTimeoutsRef = useRef({});

  // Use useUnifiedFetch to fetch live EPIs when modal opens
  const { fetchData: fetchLiveEpis, isLoading: isFetchingLiveEpis, data: liveEpisData } = useUnifiedFetch(
    `boxes/${boxDetails._id}/fetchLiveEpis`,
    false // Don't auto-fetch on mount, we'll trigger it manually
  );

  useEffect(() => {
    const request = async () => {
      if (isOpen) {
        // Fetch live EPIs when modal opens
        await fetchLiveEpis();
        await epiRceRequest();
      }
    };
    request();
  }, [isOpen, fetchLiveEpis]);

  // Handle the response from fetchLiveEpis
  useEffect(() => {
    if (liveEpisData) {
      console.log('Live EPIs data received:', liveEpisData);
      // You can add additional logic here to handle the live EPIs data
      // For example, update the obis list or perform other operations
    }
  }, [liveEpisData]);

  const epiRceRequest = async () => {
    const baseUrl = dataCenterShellUrls[boxDetails.datacenter];
    const socket = socketIOClient.connect(baseUrl, { query: { token } });
    socket.on('connect', async () => {
      socket.emit('EPI_RCE', {
        serialNumber: serialNumberDash,
        command: 'getEpiRecList',
        ip: boxDetails.vpnAddress,
      });
      setStateSocket(socket);
      await new Promise(resolve => {
        socket.on(`rce-${serialNumberDash}-list`, data => {
          if (data?.commands?.length > 0) {
            console.log('data.commands_______________________', data.commands);
            setRemoteCommands(data.commands);
            setSelectedCommand(data?.commands[0]?.value);
            resolve();
          }
        });
      });
    });
  };

  const setEpiRecOff = () => {
    if (stateSocket) {
      stateSocket.emit('EPI_RCE', {
        serialNumber: serialNumberDash,
        ip: boxDetails.vpnAddress,
        cleanup: true,
      });
    }

    setRemoteCommands([]);
    setCommandsData([]);
    setSelectedCommand('');
    setSelectedEPIs([]);
    setIsLoading(false);
    onCancel();
  };

  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelectedEPIs(obis.map((item, idx) => ({ index: idx, macAddress: item?.macAddress })));
    } else {
      setSelectedEPIs([]);
    }
  };

  const handleEpiChange = (e, epi) => {
    const macAddress = e.target.getAttribute('data-mac-address');
    if (e.target.checked) {
      setSelectedEPIs([...selectedEPIs, { index: epi, macAddress }]);
    } else {
      setSelectedEPIs(selectedEPIs.filter(item => item.index !== epi));
    }
  };

  const handleCommandChange = e => {
    setSelectedCommand(e.target.value);
  };

  const rceDataRequest = socket => {
    const ssocket = socket ? socket : stateSocket;
    if (!ssocket) console.log('socket instance error ');
    const commandResponses = {};
    ssocket &&
      ssocket.on(`rce-${serialNumberDash}-command`, data => {
        if (data?.error) {
          toast.error(data?.error, {
            toastId: 'EPI_REC_ERROR',
          });
          return;
        }
        // Extract command and update the latest response for each command
        const { command, output } = data;
        commandResponses[command] = { output, command };

        // Convert the commandResponses object to an array for state update
        const updatedCommandsData = Object.values(commandResponses);
        setCommandsData([...commandsData, ...updatedCommandsData]);
        setIsLoading(false);
      });
  };

  // Helper function to convert MAC address format and find IP
  const getIpFromMacAddress = (macAddress, liveEpisData) => {
    if (!liveEpisData?.EPIs || !macAddress) return null;
    
    // Convert MAC address from format "00268B51B038" to "00:26:8b:51:b0:38"
    const formatMacAddress = (mac) => {
      if (!mac) return null;
      // Remove any existing colons and convert to lowercase
      const cleanMac = mac.replace(/:/g, '').toLowerCase();
      // Add colons every 2 characters
      return cleanMac.match(/.{2}/g)?.join(':');
    };
    
    const formattedMac = formatMacAddress(macAddress);
    
    // Find the IP address for this MAC address
    for (const [ip, mac] of Object.entries(liveEpisData.EPIs)) {
      if (mac.toLowerCase() === formattedMac) {
        return ip;
      }
    }
    
    return null;
  };

  const onExecuteCommand = async () => {
    console.log({ selectedEPIs, selectedCommand });

    if (!selectedEPIs?.length || !selectedCommand) {
      toast.error(`Please select any EPI or Command`);
      return;
    }

    // Extract macAddresses for easier access
    const selectedMacAddresses = selectedEPIs.map(epi => epi.macAddress);
    
    // Get IP addresses for selected MAC addresses
    const selectedIpAddresses = selectedMacAddresses.map(macAddress => 
      getIpFromMacAddress(macAddress, liveEpisData)
    ).filter(ip => ip !== null); // Remove null values
    
    // if (selectedEPIs?.length > 1 && ['packet_capture_start_ata', 'packet_capture_end_ata'].includes(selectedCommand)) {
    //   toast.info(`You can select only one EPI for the Packet Capture command.`);
    //   return;
    // }

    setIsLoading(true);

    if (stateSocket) {

      if (selectedCommand !== 'packet_capture_start_ata' && selectedCommand !== 'packet_capture_end_ata') {
        stateSocket.emit('EPI_RCE', {
          serialNumber: serialNumberDash,
          command: selectedCommand,
          selectedEpis: selectedIpAddresses,
          ip: boxDetails.vpnAddress,
          rec: true,
        });
      } else {
        if (selectedCommand === 'packet_capture_start_ata') {

          await Promise.all(
            selectedEPIs.map(async (epiObj) => {
              const epi = epiObj.index;
              const epiName = `EPI ${epi + 1}`;
              const startPcapResponse = await dispatch(startPcap(boxDetails.vpnAddress, [epi], token));
              epiTimeoutsRef.current[epi] = setTimeout(async () => {
                const stopPcapResponse = await dispatch(stopPcap(boxDetails.vpnAddress, [epi], token));
                if (stopPcapResponse.type !== 'STOP_PCAP_ERROR') {
                  await dispatch(downloadPcap(boxDetails?.vpnAddress, [epi], boxDetails?.serialNumber, token));

                }
                delete epiTimeoutsRef.current[epi];
              }, 10 * 60 * 1000)

              setCommandsData((prev) => [
                ...prev,
                {
                  command: `${selectedCommand} [${epiName}]`,
                  output: typeof startPcapResponse === 'string' ? startPcapResponse : 'Ata not pingable',
                },
              ]);
            })

          );

          setIsLoading(false);
        } else {
          if (selectedCommand === 'packet_capture_end_ata') {

            await Promise.all(
              selectedEPIs.map(async (epiObj) => {
                const epi = epiObj.index;
                const epiName = `EPI ${epi + 1}`;

                const stopPcapResponse = await dispatch(stopPcap(boxDetails.vpnAddress, [epi], token));

                if (stopPcapResponse.type !== 'STOP_PCAP_ERROR') {

                  await dispatch(downloadPcap(boxDetails.vpnAddress, [epi], boxDetails.serialNumber, token));

                  if (epiTimeoutsRef.current[epi]) {
                    clearTimeout(epiTimeoutsRef.current[epi]);
                    delete epiTimeoutsRef.current[epi];
                  }
                }
                setCommandsData((prev) => [
                  ...prev,
                  {
                    command: `${selectedCommand} [${epiName}]`,
                    output: typeof stopPcapResponse === 'string' ? stopPcapResponse : 'Ata not pingable',
                  },
                ]);
              })

            );

            setIsLoading(false);
          }
        }

      }
    }


    setTimeout(() => {
      rceDataRequest(stateSocket);
    }, 100);
  };

  const onClearRce = () => {
    setCommandsData([]);
  };

  const renderCrossIcon = (
    <CrossIcon width={17} height={17} style={{ cursor: 'pointer' }} onClick={() => setEpiRecOff()} />
  );

  return (
    <CustomModal
      header="EPI Remote Execution Center"
      isOpen={isOpen}
      onCancel={setEpiRecOff}
      medium
      closeHeader={renderCrossIcon}
    >
      <Row>
        <Col md={10} style={{ margin: 'auto' }}>
          <FormGroup style={{ marginLeft: '20px', display: 'flex', flexDirection: 'column', width: 'fit-content' }}>
            <Label className="mb-2" check>
              <Input
                type="checkbox"
                name="AllEPI"
                checked={selectedEPIs.length === obis?.length && obis?.length > 0}
                onChange={handleSelectAll}
              />
              <span>{`Select All`}</span>
            </Label>
            {obis?.map((item, idx) => (
              <Label className="mb-2" check key={idx}>
                <Input
                  type="checkbox"
                  name={`EPI${idx + 1}`}
                  checked={selectedEPIs.some(selected => selected.index === idx)}
                  onChange={e => handleEpiChange(e, idx)}
                  data-mac-address={item?.macAddress}
                />
                <span>{`EPI ${idx + 1}`}</span>
              </Label>
            ))}
          </FormGroup>
          <FormGroup>
            <Label className="mb-2">
              {remoteCommands?.length > 0 && (
                <>
                  <strong>Remote Commands</strong>
                  <Input
                    type="select"
                    name="remoteCommands"
                    value={selectedCommand}
                    onChange={handleCommandChange}
                  >
                    {remoteCommands?.map(cmd => (
                      <option key={cmd?.value} value={cmd?.value}>
                        {cmd?.name}
                      </option>
                    ))}
                  </Input>
                </>
              )}
            </Label>
            {!remoteCommands?.length && 'Please wait, fetching commands.'}
            <Button color="primary" className="ml-2" onClick={onExecuteCommand} disabled={!selectedEPIs.length || !selectedCommand}>
              Execute
            </Button>
          </FormGroup>
        </Col>
      </Row>

      {commandsData?.length > 0 && (
        <>
          {commandsData.map((item = {}) => {
            if (!item?.command) return;
            return (
              <>
                <h1 className="log-heading"> {item?.command}</h1>
                <pre style={{ color: '#FFF', background: '#000', padding: '0.5rem' }}>{item.output}</pre>
              </>
            );
          })}
          <Button color="primary" onClick={onClearRce} style={{ marginTop: 15 }}>
            Clear data
          </Button>
        </>
      )}
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <LoadingIcon width={50} height={50} />
        </div>
      )}
    </CustomModal>
  );
};

export default EpiRecModal;
